import type { FloatWindowVO } from "@/utils/hooks/useResourceFloat";
import Track from "@/utils/track";
import useNavigation from "@/utils/useNavigate";
import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "./index.module.less";

const FLOAT_SIZE = 56; // 浮标尺寸
const DEFAULT_RIGHT = 2; // 默认距离右边距离
const DEFAULT_TOP = 300; // 默认距离顶部距离

/**
 * 获取 TabBar 高度（包含安全区域）
 */
const getTabBarHeight = (): number => {
  const tabBar = document.getElementById("tab-bar-bottom");
  if (tabBar) {
    return tabBar.offsetHeight;
  }
  return 0;
};

interface Props {
  data?: FloatWindowVO;
  /** 距离顶部的偏移量，默认 300 */
  yOffset?: number;
  /** 是否避开底部 TabBar，默认 true */
  avoidTabBar?: boolean;
  /** 额外的底部安全距离，默认 0 */
  extraBottomOffset?: number;
  trackData?: Record<string, any>;
  onClick?: () => void;
}

const ResourceMovable: React.FC<Props> = ({
  data,
  yOffset = DEFAULT_TOP,
  avoidTabBar = true,
  extraBottomOffset = 0,
  trackData,
  onClick,
}) => {
  const { navigateForResourceFloat } = useNavigation();
  const track = Track();
  // 初始位置：右边 2px，顶部 yOffset
  const [position, setPosition] = useState(() => ({
    right: DEFAULT_RIGHT,
    top: yOffset,
  }));
  const [isDragging, setIsDragging] = useState(false);
  const [bottomOffset, setBottomOffset] = useState(0);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    initialRight: number;
    initialTop: number;
  }>({
    startX: 0,
    startY: 0,
    initialRight: DEFAULT_RIGHT,
    initialTop: yOffset,
  });

  const handleTrack = useCallback(
    (eventName: string, extra?: any) => {
      if (!trackData || !extra?.tracking_plan_id) {
        return;
      }
      const params = { ...trackData, ...extra, tracking_plan_ids: null };
      console.log("ResourceMovable track:", eventName, params);
      track.track(eventName, params);
    },
    [trackData, track],
  );
  // 曝光标记，确保只曝光一次
  const hasExposedRef = useRef(false);
  useEffect(() => {
    if (!data?.imgUrl) {
      return;
    }
    // 如果是商品详情，必须有spu_id才能曝光
    if (trackData?.resource_name === "商详浮窗" && !trackData?.spu_id) {
      return;
    }
    // 如果是档期页浮窗，必须有activity_id才能曝光
    if (trackData?.resource_name === "档期页浮窗" && !trackData?.activity_id) {
      return;
    }
    // 已曝光则跳过
    if (hasExposedRef.current) {
      return;
    }
    handleTrack("resource_expose", {
      tracking_plan_id: trackData?.tracking_plan_ids?.expose,
    });
  }, [data?.imgUrl, trackData]);

  // 动态获取底部安全距离
  useEffect(() => {
    const updateBottomOffset = () => {
      const tabBarHeight = avoidTabBar ? getTabBarHeight() : 0;
      setBottomOffset(tabBarHeight + extraBottomOffset);
    };

    // 初始获取
    updateBottomOffset();

    // 延迟再获取一次，确保 TabBar 已渲染
    const timer = setTimeout(updateBottomOffset, 300);

    return () => clearTimeout(timer);
  }, [avoidTabBar, extraBottomOffset]);

  // 没有数据时不渲染
  if (!data?.imgUrl) {
    return null;
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    dragRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      initialRight: position.right,
      initialTop: position.top,
    };
    setIsDragging(false);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    const deltaX = touch.clientX - dragRef.current.startX;
    const deltaY = touch.clientY - dragRef.current.startY;

    // 移动超过 5px 认为是拖动
    if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
      setIsDragging(true);
    }

    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    // 计算新位置（right 减少 deltaX，因为向右移动时 right 应该减小）
    let newRight = dragRef.current.initialRight - deltaX;
    let newTop = dragRef.current.initialTop + deltaY;

    // 边界限制
    newRight = Math.max(0, Math.min(newRight, screenWidth - FLOAT_SIZE));
    // 底部边界需要减去 bottomOffset（TabBar 高度等）
    newTop = Math.max(0, Math.min(newTop, screenHeight - FLOAT_SIZE - bottomOffset));

    setPosition({
      right: newRight,
      top: newTop,
    });
  };

  const handleTouchEnd = () => {
    // 停留在当前位置，不做吸附处理
  };

  const handleClick = () => {
    if (isDragging) {
      return; // 拖动时不触发点击
    }
    onClick?.();
    // 根据 jumpType 处理跳转逻辑
    if (data.jumpType && data.jumpType !== "NOTHING") {
      navigateForResourceFloat(data.jumpType, data.jumpTarget);
    }
    handleTrack("resource_click", {
      tracking_plan_id: trackData?.tracking_plan_ids?.click,
      btn_name: "查看详情",
    });
  };

  return (
    <div
      className={styles.floatBtn}
      style={{
        right: position.right,
        top: position.top,
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onClick={handleClick}>
      <img className={styles.floatImg} src={data.imgUrl} alt="" />
    </div>
  );
};

export default ResourceMovable;
